import { throwOnMutationError, throwOnQueryError } from '../apollo-response';
import { DelegatingApolloClient } from '../delegating-apollo-client';

import {
  type ListCommentQuery,
  type CreateCommentMutation,
  type CreateCommentMutationInput,
  type ListCommentQueryInput,
  type ConfluenceComment,
  type UpdateCommentMutation,
  type UpdateCommentMutationInput,
  type DeleteCommentMutation,
  type DeleteCommentMutationInput,
  type ResolveCommentMutation,
  type ResolveCommentMutationInput,
  type ConfluenceSaveReactionResponse,
  type ConfluenceReactionsSummaryResponse,
  type ConfluenceReactedUsersResponse,
  type CreateOrDeleteCommentReactionInput,
  type CreateCommentReactionMutation,
  type DeleteCommentReactionMutation,
  type ListCommentReactionInput,
  type ListCommentReactionQuery,
  type GetCommentReactionDetailsInput,
  type GetCommentReactionDetailsQuery,
  type ContentTypesHeaderQuery,
  type ContentTypesHeaderQueryInput,
  type ListCommentResponse,
  type CreateMentionNotificationMutationInput,
  type CreateMentionNotificationMutation,
  type ContentPermissionsInput,
  type ContentPermissionsQueryResponse,
  type ContentPermissionsResponse,
  type ContentPermissionsUser,
} from './confluence-client-types';
import * as ConfluenceCreateCommentReactionMutation from './queries/confluence-mutation-create-comment-reaction.graphql';
import * as ConfluenceCreateCommentMutation from './queries/confluence-mutation-create-comment.graphql';
import * as ConfluenceCreateMentionNotificationMutation from './queries/confluence-mutation-create-mention-notification.graphql';
import * as ConfluenceDeleteCommentReactionMutation from './queries/confluence-mutation-delete-comment-reaction.graphql';
import * as ConfluenceDeleteCommentMutation from './queries/confluence-mutation-delete-comment.graphql';
import * as ConfluenceResolveCommentMutation from './queries/confluence-mutation-resolve-comment.graphql';
import * as ConfluenceUpdateCommentMutation from './queries/confluence-mutation-update-comment.graphql';
import * as ConfluenceContentTypesHeaderQuery from './queries/confluence-query-content-types-header.graphql';
import * as ConfluenceGetCommentCountQuery from './queries/confluence-query-get-comment-count.graphql';
import * as ConfluenceGetCommentReactionDetailsQuery from './queries/confluence-query-get-comment-reaction-details.graphql';
import * as ConfluenceContentPermissionsQuery from './queries/confluence-query-get-content-permissions.graphql';
import * as ConfluenceListCommentReactionQuery from './queries/confluence-query-list-comment-reactions.graphql';
import * as ConfluenceListCommentQuery from './queries/confluence-query-list-comments.graphql';

/**
 * The whiteboard (or containing content) in which the comment will live.
 */
type ContainerId = string;

/**
 * A reference to a top-level comment.
 */
type CommentId = string;
/**
 * An optional reference to a parent which is a comment.
 */
type ParentCommentId = string;

/**
 * The ADF which represents the comment's contents.
 */
type AdfContent = string;

type NotificationAction = 'NOTIFY' | 'DONT_NOTIFY';

interface AddCommentOpts {
  baseUrl: string;
  containerId: ContainerId;
  parentCommentId?: ParentCommentId;
  content: AdfContent;
  createdFrom?: 'WHITEBOARD' | 'DATABASE';
  originalSelection?: string;
}

interface UpdateCommentOpts {
  baseUrl: string;
  commentId: CommentId;
  parentCommentId?: string;
  content: AdfContent;
}

interface DeleteCommentOpts {
  baseUrl: string;
  commentId: CommentId;
}

interface ResolveCommentOpts {
  baseUrl: string;
  commentId: CommentId;
  resolved: boolean;
}

interface ListCommentOpts {
  baseUrl: string;
  containerId: ContainerId;
  parentCommentId?: ParentCommentId;
  last?: number;
  noCache?: boolean;
  before?: string;
}

interface CommentReactionOpts {
  baseUrl: string;
  containerId: ContainerId;
  commentId: CommentId;
  emojiId: string;
  containerType?: string;
}

interface ListCommentReactionOpts {
  baseUrl: string;
  comments: {
    containerId: ContainerId;
    commentId: CommentId;
  }[];
}

interface BoardTitleOpts {
  baseUrl: string;
  contentId: string;
}

interface Mention {
  mentionLocalId: string;
  mentionedUserAccountId: string;
  notificationAction: NotificationAction;
}

interface MentionNotificationOpts {
  baseUrl: string;
  contentId: string;
  mentions: Mention[];
}

interface ContentPermissionsOpts {
  baseUrl: string;
  accountIds: string[];
  contentId: string;
}

const isLegacyId = (id: string | undefined) => /^\d+$/.test(id ?? '');

type IdLocation = 'marker' | 'id';

export class ConfluenceClient {
  /**
   * Whiteboards aim to maintain loose coupling with external systems.
   * {@link referenceIdToContentIdMapping} maintains a mapping between a UUID
   * and externally owned ID for the purposes of loose coupling.
   *
   * @description Use this mapping via {@link toContentId} to go from UUID ->
   * external ID for API calls (most will still use external ID)
   * @description Use this mapping via {@link withReferenceId} to go from external
   * ID -> UUID for API calls (most will still use external ID)
   */
  private referenceIdToContentIdMapping = new Map<string, string>();
  private contentIdToReferenceIdMapping = new Map<string, string>();

  private readonly clients: DelegatingApolloClient = new DelegatingApolloClient();

  constructor() {}

  public async addComment({
    baseUrl,
    containerId,
    content,
    parentCommentId,
    createdFrom = 'WHITEBOARD',
    originalSelection = '',
  }: AddCommentOpts): Promise<ConfluenceComment> {
    const client = this.clients.getOrCreate(baseUrl, '/cgraphql');
    const contentId = parentCommentId ? this.toContentId(parentCommentId) : undefined;

    const responseOrErr = await client
      .mutate<CreateCommentMutation['data'], CreateCommentMutationInput>({
        mutation: ConfluenceCreateCommentMutation.CreateInlineComment,
        variables: {
          input: {
            containerId: containerId.toString(),
            parentCommentId: contentId,
            createdFrom: createdFrom,
            commentBody: {
              value: content,
              representationFormat: 'ATLAS_DOC_FORMAT',
            },
            lastFetchTimeMillis: Date.now(),
            matchIndex: 0,
            numMatches: 1,
            originalSelection,
          },
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => err);

    throwOnMutationError(responseOrErr);

    const idLocation: IdLocation = parentCommentId ? 'id' : 'marker';
    return this.withReferenceId(responseOrErr.data.createInlineComment, idLocation);
  }

  public async updateComment({
    baseUrl,
    parentCommentId,
    commentId,
    content,
  }: UpdateCommentOpts): Promise<ConfluenceComment> {
    const client = this.clients.getOrCreate(baseUrl, '/cgraphql');
    const contentId = this.toContentId(commentId);

    const responseOrErr = await client
      .mutate<UpdateCommentMutation['data'], UpdateCommentMutationInput>({
        mutation: ConfluenceUpdateCommentMutation.UpdateInlineComment,
        variables: {
          input: {
            commentId: contentId,
            commentBody: {
              value: content,
              representationFormat: 'ATLAS_DOC_FORMAT',
            },
          },
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => err);

    throwOnMutationError(responseOrErr);

    const idLocation: IdLocation = parentCommentId ? 'id' : 'marker';
    return this.withReferenceId(responseOrErr.data.updateComment, idLocation);
  }

  public async deleteComment({ baseUrl, commentId }: DeleteCommentOpts): Promise<void> {
    const client = this.clients.getOrCreate(baseUrl, '/cgraphql');
    const contentId = this.toContentId(commentId);

    const responseOrErr = await client
      .mutate<DeleteCommentMutation['data'], DeleteCommentMutationInput>({
        mutation: ConfluenceDeleteCommentMutation.DeleteInlineComment,
        variables: {
          commentIdToDelete: contentId,
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => err);

    throwOnMutationError(responseOrErr);

    if (responseOrErr.data.deleteComment === false) {
      throw new Error(`failed deletion: ${JSON.stringify(responseOrErr.errors)}`);
    }

    return;
  }

  public async resolveComment({
    baseUrl,
    commentId,
    resolved,
  }: ResolveCommentOpts): Promise<void> {
    const client = this.clients.getOrCreate(baseUrl, '/cgraphql');
    const contentId = this.toContentId(commentId);

    const responseOrErr = await client
      .mutate<ResolveCommentMutation['data'], ResolveCommentMutationInput>({
        mutation: ConfluenceResolveCommentMutation.ResolveInlineComment,
        variables: { commentId: contentId, resolved },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => err);

    throwOnMutationError(responseOrErr);

    if (responseOrErr.data.resolveInlineComment.status === false) {
      throw new Error(`failed resolve: ${JSON.stringify(responseOrErr.errors)}`);
    }

    return;
  }

  public async listComments({
    baseUrl,
    containerId,
    parentCommentId,
    last,
    noCache = false,
    before,
  }: ListCommentOpts): Promise<ListCommentResponse> {
    const client = this.clients.getOrCreate(baseUrl, '/cgraphql');

    const variables: ListCommentQueryInput = {
      contentId: containerId.toString(),
      last: last ?? 50,
      offset: before ?? undefined,
    };

    if (isLegacyId(parentCommentId)) {
      variables.commentId = parentCommentId;
    } else if (parentCommentId) {
      variables.inlineMarkerRef = parentCommentId;
    }

    const responseOrErr = await client
      .query<ListCommentQuery['data'], ListCommentQueryInput>({
        query: ConfluenceListCommentQuery.CommentList,
        variables,
        context: { tenantBaseUrl: baseUrl.toString() },
        fetchPolicy: noCache ? 'no-cache' : undefined,
      })
      .catch((err): Error => err);

    throwOnQueryError(responseOrErr);

    return {
      comments: responseOrErr.data.comments.edges.map((e) =>
        this.withReferenceId(e.node)
      ),
      totalCount: responseOrErr.data.comments.totalCount,
      cursor: responseOrErr.data.comments.edges[0].cursor,
    };
  }

  public async getCommentCount({
    baseUrl,
    containerId,
    last,
    noCache = false,
  }: ListCommentOpts): Promise<number> {
    const client = this.clients.getOrCreate(baseUrl, '/cgraphql');

    const variables: ListCommentQueryInput = {
      contentId: containerId.toString(),
      last: last ?? 50,
    };

    const responseOrErr = await client
      .query<ListCommentQuery['data'], ListCommentQueryInput>({
        query: ConfluenceGetCommentCountQuery.CommentCount,
        variables,
        context: { tenantBaseUrl: baseUrl.toString() },
        fetchPolicy: noCache ? 'no-cache' : undefined,
      })
      .catch((err): Error => err);

    throwOnQueryError(responseOrErr);

    return responseOrErr.data.comments.totalCount;
  }

  public async addCommentReaction({
    baseUrl,
    containerId,
    commentId,
    emojiId,
    containerType = 'WHITEBOARD',
  }: CommentReactionOpts): Promise<ConfluenceSaveReactionResponse> {
    const client = this.clients.getOrCreate(baseUrl, '/cgraphql');
    const contentId = this.toContentId(commentId);

    const responseOrErr = await client
      .mutate<CreateCommentReactionMutation['data'], CreateOrDeleteCommentReactionInput>({
        mutation: ConfluenceCreateCommentReactionMutation.CreateCommentReaction,
        variables: {
          contentId,
          contentType: 'COMMENT',
          containerId,
          containerType: containerType,
          emojiId,
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => err);

    throwOnMutationError(responseOrErr);

    return responseOrErr.data.addReaction;
  }

  public async deleteCommentReaction({
    baseUrl,
    containerId,
    commentId,
    emojiId,
    containerType = 'WHITEBOARD',
  }: CommentReactionOpts): Promise<ConfluenceSaveReactionResponse> {
    const client = this.clients.getOrCreate(baseUrl, '/cgraphql');
    const contentId = this.toContentId(commentId);

    const responseOrErr = await client
      .mutate<DeleteCommentReactionMutation['data'], CreateOrDeleteCommentReactionInput>({
        mutation: ConfluenceDeleteCommentReactionMutation.DeleteCommentReaction,
        variables: {
          contentId,
          contentType: 'COMMENT',
          containerId,
          containerType: containerType,
          emojiId,
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => err);

    throwOnMutationError(responseOrErr);

    return responseOrErr.data.deleteReaction;
  }

  public async listCommentReactions({
    baseUrl,
    comments,
  }: ListCommentReactionOpts): Promise<ConfluenceReactionsSummaryResponse[]> {
    const client = this.clients.getOrCreate(baseUrl, '/cgraphql');
    const responseOrErr = await client
      .query<ListCommentReactionQuery['data'], ListCommentReactionInput>({
        query: ConfluenceListCommentReactionQuery.CommentReactionList,
        variables: {
          comments: comments.map(({ containerId, commentId }) => {
            const contentId = this.toContentId(commentId);

            return {
              contentId,
              contentType: 'COMMENT',
              containerId,
              containerType: 'WHITEBOARD',
            };
          }),
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => err);

    throwOnQueryError(responseOrErr);

    return responseOrErr.data.reactionsSummaryList;
  }

  public async getCommentReactionDetails({
    baseUrl,
    containerId,
    commentId,
    emojiId,
  }: CommentReactionOpts): Promise<ConfluenceReactedUsersResponse> {
    const client = this.clients.getOrCreate(baseUrl, '/cgraphql');
    const contentId = this.toContentId(commentId);

    const responseOrErr = await client
      .query<GetCommentReactionDetailsQuery['data'], GetCommentReactionDetailsInput>({
        query: ConfluenceGetCommentReactionDetailsQuery.CommentReactionDetails,
        variables: {
          contentId,
          contentType: 'COMMENT',
          containerId,
          containerType: 'WHITEBOARD',
          emojiId,
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => err);

    throwOnQueryError(responseOrErr);

    return responseOrErr.data.reactedUsers;
  }

  public async getWhiteboardBoardTitle({
    baseUrl,
    contentId,
  }: BoardTitleOpts): Promise<string> {
    const client = this.clients.getOrCreate(baseUrl, '/cgraphql');
    const responseOrErr = await client
      .query<ContentTypesHeaderQuery['data'], ContentTypesHeaderQueryInput>({
        query: ConfluenceContentTypesHeaderQuery.ContentTypesHeaderQuery,
        variables: {
          contentId,
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => {
        console.log(err);
        return err;
      });

    throwOnQueryError(responseOrErr);

    return responseOrErr.data.content.nodes[0].title;
  }

  public async createMentionNotification({
    baseUrl,
    contentId,
    mentions,
  }: MentionNotificationOpts): Promise<void> {
    const client = this.clients.getOrCreate(baseUrl, '/cgraphql');
    const responseOrErr = await client
      .mutate<CreateMentionNotificationMutation, CreateMentionNotificationMutationInput>({
        mutation: ConfluenceCreateMentionNotificationMutation.CreateMentionNotification,
        variables: {
          contentId,
          mentions,
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => {
        return err;
      });

    throwOnMutationError(responseOrErr);

    return;
  }

  public async getContentPermissions({
    baseUrl,
    accountIds,
    contentId,
  }: ContentPermissionsOpts): Promise<ContentPermissionsResponse> {
    const client = this.clients.getOrCreate(baseUrl, '/cgraphql');
    const responseOrErr = await client
      .query<ContentPermissionsQueryResponse, ContentPermissionsInput>({
        query: ConfluenceContentPermissionsQuery.ContentPermissionsQuery,
        variables: {
          accountIds,
          contentId,
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => {
        return err;
      });

    throwOnQueryError(responseOrErr);

    const users = new Map<string, ContentPermissionsUser>();
    const contentPermissionsData = responseOrErr.data;
    // `contentPermissions` specifies restrictions on the current item
    // `usersWithContentRestrictions` specifies restrictions on the current item's parent, if one exists
    const { contentPermissions, usersWithContentRestrictions } = contentPermissionsData;

    if (contentPermissions) {
      const usersWithAccess =
        contentPermissions.subjectsWithEffectiveContentPermissions?.edges;

      for (const userWithContentRestrictions of usersWithContentRestrictions) {
        const { accountId, profilePicture, hasSpaceViewPermission, restrictingContent } =
          userWithContentRestrictions;
        let userHasAccessToContent = false;
        let isParentDatabaseRestricted = false;
        let isSpaceRestricted = false;

        if (!hasSpaceViewPermission) {
          isSpaceRestricted = true;
        } else if (restrictingContent?.hasRestrictions) {
          // Parent item has content restrictions, which is inherited by this child item
          isParentDatabaseRestricted = true;
        } else {
          // Not a parent item, check permissions on this item
          userHasAccessToContent =
            usersWithAccess?.some(
              (user) => user.node.id === userWithContentRestrictions.accountId
            ) || contentPermissions.contentAccess !== 'EVERYONE_NO_ACCESS';
        }

        users.set(accountId, {
          accountId: accountId,
          profilePicture,
          userHasAccessToContent,
          isParentDatabaseRestricted,
          isSpaceRestricted,
        });
      }
    }

    return Array.from(users.values());
  }

  public toReferenceId(contentId: string) {
    return this.contentIdToReferenceIdMapping.get(contentId) ?? contentId;
  }

  private toContentId(referenceId: string) {
    return this.referenceIdToContentIdMapping.get(referenceId) ?? referenceId;
  }

  private withReferenceId(
    node: ConfluenceComment,
    idLocation: IdLocation = 'marker'
  ): ConfluenceComment {
    const contentId = node.id;
    const referenceId = idLocation === 'marker' ? node.location.inlineMarkerRef : node.id;

    this.referenceIdToContentIdMapping.set(referenceId, contentId);
    this.contentIdToReferenceIdMapping.set(contentId, node.location.inlineMarkerRef);

    return {
      ...node,
      id: referenceId,
      reactionsId: node.id,
      replies: node.replies?.map((r) => this.withReferenceId(r, 'id')),
    };
  }
}
